import request from '@oskecare/fe-utilities/lib/request';
import {
  STAT_CATEGORY_ORDERS,
  STAT_GROUP_SHS_ORDER,
} from '@oskecare/fe-utilities/lib/constants/stats';

/**
 * Module for retrieving data sharing related information.
 * This is required for data sharing widgets and for augmenting OST service list.
 *
 * @property {CustomerDataManager} _customerDataManager
 * @property {Products} _products
 * @property {Logger} _logger
 */
export default class DataSharing {
  /**
   * @param {CustomerDataManager} customerDataManager
   * @param {Products} products
   * @param {Logger} logger
   */
  constructor({ customerDataManager, products, logger }) {
    this._customerDataManager = customerDataManager;
    this._products = products;
    this._logger = logger;
  }

  /**
   * Proxy for `request` function with logSource pre-set
   *
   * @param {object} params
   * @return {Promise<*>}
   * @see Request.fetch
   * @private
   */
  request(params) {
    return request({
      ...params,
      logSource: 'infra-data-sharing',
    }).catch(this._logger.catchError);
  }

  /**
   * Fetch customer product data from API or cache
   *
   * TODO: Move parsing to infrastructure
   *
   * @param {string} subscriptionId
   * @param {boolean} isNonessential
   * @returns {Promise<?Object>}
   */
  async getCustomerProduct(subscriptionId, isNonessential = false) {
    // TODO: rewrite method to accept SN directly
    const { sn } = (await this._customerDataManager.getCustomerData()).getSnDataBySubscriptionId(
      subscriptionId,
    );

    const productList = await this._products.inventory.getSharingServices({ sn, isNonessential });

    if (!productList.hasItems) {
      return null;
    }

    return productList.singleItem?._data;
  }

  /**
   * Fetch product offering data from API or cache.
   * For active and in-progress groups use groupId instead of productId.
   *
   * TODO: Move parsing to infrastructure, rewrite it to work with ProductList model directly, current implementation
   * was made to mirror the legacy API and minimize impact on parsing.
   *
   * @param {string} productId
   * @param {?string} groupId
   * @param {boolean} isNonessential
   * @returns {Promise<?Object>}
   */
  async getProductOffering({ productId, groupId = null, isNonessential = false }) {
    const productList = await this._products.offering.getSharingService({
      productSpecificationId: productId,
      productId: groupId,
      isNonessential,
    });

    const contributorListProduct = productList.getItemByPredicate(product =>
      product.characteristics.every(characteristic => characteristic.name === 'shsContributor'),
    );

    const memberListProduct = productList.getItemByPredicate(product =>
      product.characteristics.every(characteristic => characteristic.name === 'shsContributor'),
    );

    return {
      shsMemberList:
        memberListProduct?.characteristics.map(characteristic => characteristic.value) ?? [],
      shsContributorList:
        contributorListProduct?.characteristics.map(characteristic => characteristic.value) ?? [],
    };
  }

  /**
   * Submit an order.
   *
   * @param {string} workingSn
   * @param {Object[]} order
   * @return {Promise<*>}
   */
  async submitOrder(workingSn, order) {
    return this._products.order.submitSharingService(workingSn, order).then(data => {
      this._logger.stat(
        'SHS Order',
        STAT_GROUP_SHS_ORDER,
        STAT_CATEGORY_ORDERS,
        'infra-data-sharing',
      );
      return data;
    });
  }

  /**
   * Request cancellation of the unprocessed order that's currently in progress. Only use when the order
   * has `arePendingChangesCancellable` flag set to `true`.
   *
   * @param {string} workingSn
   * @param {string} groupId
   * @return {Promise<*>}
   */
  async resetInProgressChanges(workingSn, groupId) {
    return this._products.order.cancelSharingServiceChanges(workingSn, groupId).then(data => {
      this._logger.stat(
        'SHS Changes Cancelled',
        STAT_GROUP_SHS_ORDER,
        STAT_CATEGORY_ORDERS,
        'infra-data-sharing',
      );
      return data;
    });
  }
}
